import React, { useEffect, useMemo, useState } from "react";

import { Button, Col, Form, Modal, Row } from "react-bootstrap";

import classNames from "classnames";
import { isValidPhoneNumber } from "libphonenumber-js";
import { Control, Controller, FieldErrors, useForm, useWatch } from "react-hook-form";

import { RelatedUsersFragment, UpdateUserDetailsInput, UserStatus, useUpdateUserDetailsMutation } from "@api/graphql";
import { ToastMessage } from "@components/ToastMessage";
import { FormErrorText } from "@components/form/FormErrorText";
import { Loading } from "@components/loading";
import { formatDateStringLong } from "@util/index";
import { E164_MOBILE_NUMBER_FORMAT, EMAIL } from "@util/regex";

import styles from "./RelatedUsersModal.module.scss";

interface RelatedUsersModalPropType {
  isOpen: boolean;
  onClose: () => void;
  selectedUser: RelatedUsersFragment;
  refetchAccountDetails: () => void;
}
interface ContactDetailsPropType {
  control: Control<UpdateUserDetailsInput>;
  errors: FieldErrors<UpdateUserDetailsInput>;
  selectedUser: RelatedUsersFragment;
  loading: boolean;
  onClose: () => void;
}

const ContactDetails = ({ control, errors, selectedUser, loading, onClose }: ContactDetailsPropType) => {
  const currentStatus = useWatch({ control, name: "status" }) || UserStatus.ACTIVE;
  const isActiveStatus = currentStatus === UserStatus.ACTIVE;

  return (
    <>
      <Row className="mb-4">
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>Title</Form.Label>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <Form.Control {...field} as="select" className={field.value ? "custom-select-value-selected" : ""}>
                  <option hidden value="">
                    Select your title
                  </option>
                  <option value="Mr.">Mr.</option>
                  <option value="Ms.">Ms.</option>
                  <option value="Mrs.">Mrs.</option>
                  <option value="Dr.">Dr.</option>
                </Form.Control>
              )}
            />
          </Form.Group>
        </Col>
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>* First name</Form.Label>
            <Controller
              name="first_name"
              control={control}
              rules={{ required: "First name is required" }}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  placeholder="Enter your first name"
                  isInvalid={!!errors?.first_name}
                />
              )}
            />
            <FormErrorText error={errors?.first_name} />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>Middle name</Form.Label>
            <Controller
              name="middle_name"
              control={control}
              render={({ field }) => <Form.Control {...field} type="text" placeholder="Enter your middle name" />}
            />
          </Form.Group>
        </Col>
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>{isActiveStatus ? "* " : ""}Last name</Form.Label>
            <Controller
              name="last_name"
              control={control}
              rules={isActiveStatus ? { required: "Last name is required" } : {}}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  placeholder="Enter your last name"
                  isInvalid={!!errors?.last_name}
                />
              )}
            />
            <FormErrorText error={errors?.last_name} />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>Preferred pronoun</Form.Label>
            <Controller
              name="pronouns"
              control={control}
              render={({ field }) => <Form.Control {...field} type="text" placeholder="Enter your preferred pronoun" />}
            />
          </Form.Group>
        </Col>
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>Preferred name</Form.Label>
            <Controller
              name="preferred_name"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  placeholder="Enter your preferred name"
                  isInvalid={!!errors?.preferred_name}
                />
              )}
            />
            <FormErrorText error={errors?.preferred_name} />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>* Email</Form.Label>
            <Controller
              name="email"
              control={control}
              rules={{
                required: "Email is required",
                pattern: {
                  value: EMAIL,
                  message: "Please enter a valid email address",
                },
              }}
              render={({ field }) => (
                <Form.Control {...field} type="email" placeholder="Enter your email" isInvalid={!!errors?.email} />
              )}
            />
            <FormErrorText error={errors?.email} />
          </Form.Group>
        </Col>
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>{isActiveStatus ? "* " : ""}Mobile number</Form.Label>
            <Form.Text className="text-muted d-block mb-2" style={{ fontSize: "0.75rem" }}>
              Enter a mobile phone number, including the country code for example +61412345678
            </Form.Text>
            <Controller
              name="phone_number"
              control={control}
              rules={{
                required: isActiveStatus ? "Mobile number is required" : false,
                validate: (value) => {
                  const errorMessage = `Oops, that number isn't in the right format. Please start with a plus sign '+' and add the country code ('61' for Australia), omit the first '0', then write the mobile number (no spaces)`;
                  try {
                    if (value && (!E164_MOBILE_NUMBER_FORMAT.test(value) || !isValidPhoneNumber(value))) {
                      return errorMessage;
                    }
                    return true;
                  } catch (error) {
                    return errorMessage;
                  }
                },
              }}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  step={1}
                  min="0"
                  placeholder="Enter mobile number"
                  value={field.value || "+61"}
                  isInvalid={!!errors?.phone_number}
                  onWheel={(event) => event.currentTarget.blur()}
                />
              )}
            />
            <FormErrorText error={errors?.phone_number} />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col md={6}>
          <Form.Group controlId="start_date">
            <Form.Label>Start Date</Form.Label>
            <div>
              {selectedUser.start_date ? formatDateStringLong(new Date(selectedUser.start_date).toISOString()) : "-"}
            </div>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group controlId="start_date">
            <Form.Label>End Date</Form.Label>
            <div>
              {selectedUser.end_date ? formatDateStringLong(new Date(selectedUser.end_date).toISOString()) : "-"}
            </div>
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>* Status</Form.Label>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Form.Select
                  {...field}
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value as UserStatus)}
                  disabled={selectedUser.user_status === UserStatus.INACTIVE}
                >
                  <option value={UserStatus.ACTIVE}>{UserStatus.ACTIVE}</option>
                  <option value={UserStatus.INACTIVE}>{UserStatus.INACTIVE}</option>
                  <option value={UserStatus.INVITED} disabled>
                    {UserStatus.INVITED}
                  </option>
                  <option value={UserStatus.PENDING_APPROVAL}>{UserStatus.PENDING_APPROVAL}</option>
                </Form.Select>
              )}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Form.Group>
          <Controller
            name="receive_newsletter"
            control={control}
            render={({ field }) => {
              return (
                <Form.Check
                  className={classNames(styles["form-check"], "pt-0 pb-2 ps-4 mb-2")}
                  type="checkbox"
                  label={
                    <div className="b4 medium monoGray1 d-flex align-items-center h-100">
                      I’d like to receive the newsletter with resale royalty news and updates
                    </div>
                  }
                  checked={field.value || false}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              );
            }}
          />
        </Form.Group>
      </Row>
      {loading && <Loading />}
      <Row className="mb-4">
        <Col className="d-flex justify-content-center mb-4 mt-4">
          <Button type="submit" variant="primary" className="me-2">
            Save
          </Button>
          <Button variant="primary" onClick={onClose}>
            Cancel
          </Button>
        </Col>
      </Row>
    </>
  );
};

export const RelatedUsersModal = ({
  isOpen,
  onClose,
  selectedUser,
  refetchAccountDetails,
}: RelatedUsersModalPropType) => {
  const defaultValues = useMemo(() => {
    const {
      title = "",
      first_name = "",
      middle_name = "",
      last_name = "",
      pronouns = "",
      preferred_name = "",
      email = "",
      phone_number = "",
      user_status,
      receive_newsletter = false,
    } = selectedUser;

    return {
      title,
      first_name,
      middle_name,
      last_name,
      pronouns,
      preferred_name,
      email,
      phone_number,
      status: user_status === UserStatus.INVITED ? UserStatus.ACTIVE : user_status,
      receive_newsletter,
    };
  }, [selectedUser]);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<UpdateUserDetailsInput>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues,
  });
  const [updateUserDetailsMutation, { loading }] = useUpdateUserDetailsMutation({});
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      reset({
        ...defaultValues,
      });
    }
  }, [isOpen, reset, defaultValues]);

  const submitHandler = async (data: UpdateUserDetailsInput) => {
    try {
      const res = await updateUserDetailsMutation({
        variables: {
          input: {
            user_id: selectedUser.user_id ?? -1,
            title: data.title,
            first_name: data.first_name,
            last_name: data.last_name,
            middle_name: data.middle_name,
            phone_number: data.phone_number,
            email: data.email,
            preferred_name: data.preferred_name,
            pronouns: data.pronouns,
            receive_newsletter: data.receive_newsletter,
            status: data.status,
          },
        },
      });
      if (res?.data?.updateUserDetails?.success) {
        setToastMessage("User details updated successfully.");
        setShowToast(true);
        setIsSuccess(true);
        onClose();
        refetchAccountDetails();
      } else {
        setToastMessage("Error updating user details.");
        setShowToast(true);
        setIsSuccess(false);
      }
    } catch (error) {
      setToastMessage(`Error updating user details ${error}`);
      setShowToast(true);
      setIsSuccess(false);
    }
  };
  return (
    <>
      <ToastMessage
        message={toastMessage}
        show={showToast}
        setShow={setShowToast}
        type={isSuccess ? "success" : "error"}
      />
      <Modal show={isOpen} size="lg" onHide={onClose}>
        <Modal.Header closeButton className={styles["modal-header"]}>
          <h3>{selectedUser.full_name}</h3>
        </Modal.Header>
        <Modal.Body className="p-5">
          <Form onSubmit={handleSubmit(submitHandler)}>
            <h4>Contact Details</h4>
            <ContactDetails
              control={control}
              errors={errors}
              selectedUser={selectedUser}
              loading={loading}
              onClose={onClose}
            />
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};
